import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AIRelanceGenerator from './AIRelanceGenerator';
import EnhancedDashboard from './EnhancedDashboard';
import ObjectivesManager from './ObjectivesManager';
import './css/BaobabDesignSystem.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = ({ onNavigate }) => {
  const [enhancedData, setEnhancedData] = useState(null);
  const [conversionData, setConversionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [objectives, setObjectives] = useState([]);
  const [showObjectiveModal, setShowObjectiveModal] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [activeTab, setActiveTab] = useState('enhanced'); // enhanced, classic, performance, pipeline
  const [timeframe, setTimeframe] = useState('month'); // week, month, quarter
  const [healthScore, setHealthScore] = useState(0);
  const [priorityActions, setPriorityActions] = useState([]);
  const [showAIRelanceModal, setShowAIRelanceModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [objectiveForm, setObjectiveForm] = useState({
    title: '',
    description: '',
    target_value: '',
    unit: 'FCFA',
    category: 'revenue',
    period: 'monthly',
    deadline: ''
  });

  useEffect(() => {
    fetchEnhancedData();
    fetchConversionData();
    fetchObjectives();
  }, []);

  useEffect(() => {
    if (enhancedData && conversionData) {
      const score = calculateHealthScore();
      setHealthScore(score);
      setPriorityActions(calculatePriorityActions());
    }
  }, [enhancedData, conversionData]);

  const fetchEnhancedData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/enhanced`, {
        timeout: 10000, // 10 secondes timeout
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data) {
        setEnhancedData(response.data);
      } else {
        console.warn('Dashboard data is empty');
        // Données par défaut si vide
        setEnhancedData({
          total_operations: 0,
          total_revenue: 0,
          active_clients: 0,
          pending_opportunities: 0,
          last_update: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error fetching enhanced dashboard data:', error);
      // Si des données existent déjà, les conserver (erreur lors du refresh)
      // Sinon, définir des données par défaut avec message d'erreur
      if (!enhancedData) {
        setEnhancedData({
          total_operations: 0,
          total_revenue: 0,
          active_clients: 0,
          pending_opportunities: 0,
          error: 'Impossible de charger les données. Veuillez actualiser la page.',
          last_update: new Date().toISOString()
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConversionData = async () => {
    try {
      // Configuration headers avec token
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      
      // Fetch opportunities
      const opportunitiesResponse = await axios.get(`${API}/opportunities`, { headers });
      const opportunities = opportunitiesResponse.data;
      
      // Fetch documents
      const documentsResponse = await axios.get(`${API}/documents`, { headers });
      const documents = documentsResponse.data;
      
      // Calculate conversion metrics
      const documentsWithOpportunity = documents.filter(d => d.opportunite_id);
      const conversionRate = opportunities.length > 0 ? (documentsWithOpportunity.length / opportunities.length) * 100 : 0;
      
      // Pipeline stages
      const pipeline = {
        opportunities: opportunities.length,
        devis: documents.filter(d => d.type === 'devis' && d.opportunite_id).length,
        bon_commande: documents.filter(d => d.type === 'bon_commande' && d.opportunite_id).length,
        facture: documents.filter(d => d.type === 'facture' && d.opportunite_id).length
      };
      
      // Recent conversions
      const recentConversions = opportunities
        .filter(opp => documents.some(doc => doc.opportunite_id === opp.id))
        .map(opp => {
          const oppDocuments = documents.filter(doc => doc.opportunite_id === opp.id);
          const totalValue = oppDocuments.reduce((sum, doc) => sum + (doc.total_ttc || 0), 0);
          return {
            ...opp,
            documents_count: oppDocuments.length,
            total_value: totalValue,
            latest_document: oppDocuments.sort((a, b) => new Date(b.date) - new Date(a.date))[0]
          };
        })
        .sort((a, b) => new Date(b.latest_document.date) - new Date(a.latest_document.date))
        .slice(0, 5);
      
      setConversionData({
        total_opportunities: opportunities.length,
        total_documents: documents.length,
        documents_with_opportunity: documentsWithOpportunity.length,
        conversion_rate: conversionRate,
        pipeline_stages: pipeline,
        recent_conversions: recentConversions,
        orphan_documents: documents.filter(d => !d.opportunite_id).length,
        all_documents: documents,
        all_opportunities: opportunities
      });
      
    } catch (error) {
      console.error('Error fetching conversion data:', error);
    }
  };

  // Enhanced Health Score Calculation
  const calculateHealthScore = () => {
    if (!conversionData || !enhancedData) return 0;
    
    const conversionScore = Math.min(conversionData.conversion_rate * 2, 100); // Weight conversion rate
    const pipelineScore = conversionData.pipeline_stages.opportunities > 0 ? 
      ((conversionData.pipeline_stages.devis + conversionData.pipeline_stages.bon_commande + conversionData.pipeline_stages.facture) / conversionData.pipeline_stages.opportunities) * 100 : 0;
    const activityScore = enhancedData.commercial_kpis.opportunities_this_month > 0 ? 80 : 20;
    const operationalScore = enhancedData.operational_kpis.completed_operations > 0 ? 90 : 30;
    
    return Math.round((conversionScore * 0.3 + pipelineScore * 0.3 + activityScore * 0.2 + operationalScore * 0.2));
  };

  const getHealthColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-100', text: 'text-green-800', ring: 'ring-green-200', indicator: 'bg-green-500' };
    if (score >= 60) return { bg: 'bg-yellow-100', text: 'text-yellow-800', ring: 'ring-yellow-200', indicator: 'bg-yellow-500' };
    if (score >= 40) return { bg: 'bg-orange-100', text: 'text-orange-800', ring: 'ring-orange-200', indicator: 'bg-orange-500' };
    return { bg: 'bg-red-100', text: 'text-red-800', ring: 'ring-red-200', indicator: 'bg-red-500' };
  };

  const calculatePriorityActions = () => {
    if (!conversionData || !enhancedData) return [];
    
    const actions = [];
    
    // Critical actions
    if (conversionData.orphan_documents > 0) {
      actions.push({
        type: 'critical',
        icon: '🚨',
        title: 'Documents orphelins',
        description: `${conversionData.orphan_documents} documents ne sont pas liés à des opportunités`,
        action: 'Lier aux opportunités',
        priority: 'high',
        count: conversionData.orphan_documents
      });
    }
    
    if (conversionData.conversion_rate < 50) {
      actions.push({
        type: 'warning',
        icon: '',
        title: 'Taux de conversion faible',
        description: `${conversionData.conversion_rate.toFixed(1)}% des opportunités génèrent des documents`,
        action: 'Améliorer le processus commercial',
        priority: 'high',
        count: Math.round(conversionData.total_opportunities * (50 - conversionData.conversion_rate) / 100)
      });
    }
    
    // Opportunities requiring follow-up
    const staleOpportunities = conversionData.all_opportunities?.filter(opp => {
      const daysSinceUpdate = Math.floor((new Date() - new Date(opp.date_creation)) / (1000 * 60 * 60 * 24));
      return daysSinceUpdate > 7 && opp.statut === 'en_cours';
    }) || [];
    
    if (staleOpportunities.length > 0) {
      actions.push({
        type: 'info',
        icon: '⏰',
        title: 'Opportunités à relancer',
        description: `${staleOpportunities.length} opportunités sans activité depuis plus de 7 jours`,
        action: 'Programmer relances',
        priority: 'medium',
        count: staleOpportunities.length
      });
    }
    
    if (enhancedData.commercial_kpis.opportunities_this_month === 0) {
      actions.push({
        type: 'info',
        icon: '🎯',
        title: 'Aucune nouvelle opportunité',
        description: 'Aucune opportunité créée ce mois',
        action: 'Prospection commerciale',
        priority: 'medium',
        count: 1
      });
    }
    
    return actions.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    }).slice(0, 5);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const fetchObjectives = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      const response = await axios.get(`${API}/objectives`, { headers });
      setObjectives(response.data);
    } catch (error) {
      console.error('Error fetching objectives:', error);
    }
  };

  const handleCreateObjective = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      const payload = {
        ...objectiveForm,
        target_value: parseFloat(objectiveForm.target_value),
        deadline: new Date(objectiveForm.deadline).toISOString()
      };
      await axios.post(`${API}/objectives`, payload, { headers });
      setShowObjectiveModal(false);
      setObjectiveForm({
        title: '',
        description: '',
        target_value: '',
        unit: 'FCFA',
        category: 'revenue',
        period: 'monthly',
        deadline: ''
      });
      fetchObjectives();
      fetchEnhancedData();
    } catch (error) {
      console.error('Error creating objective:', error);
      alert('Erreur lors de la création de l\'objectif. Veuillez vérifier les données.');
    }
  };

  const handleUpdateObjective = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      const payload = {
        ...objectiveForm,
        target_value: parseFloat(objectiveForm.target_value),
        deadline: new Date(objectiveForm.deadline).toISOString()
      };
      await axios.put(`${API}/objectives/${editingObjective.id}`, payload, { headers });
      setShowObjectiveModal(false);
      setEditingObjective(null);
      setObjectiveForm({
        title: '',
        description: '',
        target_value: '',
        unit: 'FCFA',
        category: 'revenue',
        period: 'monthly',
        deadline: ''
      });
      fetchObjectives();
      fetchEnhancedData();
    } catch (error) {
      console.error('Error updating objective:', error);
      alert('Erreur lors de la mise à jour de l\'objectif. Veuillez vérifier les données.');
    }
  };

  const handleDeleteObjective = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet objectif ?')) {
      try {
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        };
        await axios.delete(`${API}/objectives/${id}`, { headers });
        fetchObjectives();
        fetchEnhancedData();
      } catch (error) {
        console.error('Error deleting objective:', error);
      }
    }
  };

  const openObjectiveModal = (objective = null) => {
    if (objective) {
      setEditingObjective(objective);
      setObjectiveForm({
        title: objective.title,
        description: objective.description || '',
        target_value: objective.target_value,
        unit: objective.unit,
        category: objective.category,
        period: objective.period || 'monthly',
        deadline: objective.deadline.split('T')[0]
      });
    }
    setShowObjectiveModal(true);
  };

  const closeObjectiveModal = () => {
    setShowObjectiveModal(false);
    setEditingObjective(null);
    setObjectiveForm({
      title: '',
      description: '',
      target_value: '',
      unit: 'FCFA',
      category: 'revenue',
      period: 'monthly',
      deadline: ''
    });
  };

  const calculateProgress = (current, target) => {
    if (target === 0) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 60) return 'bg-yellow-500';
    if (progress >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'revenue': return '💰';
      case 'opportunities': return '🎯';
      case 'operations': return '📦';
      default: return '📊';
    }
  };

  const handlePriorityAction = (action) => {
    if (action.type === 'warning' && action.title === 'Documents orphelins') {
      // Navigate to opportunities with filter for orphaned documents
      if (onNavigate) {
        onNavigate('opportunities');
        // We'll need to implement a way to filter orphaned documents in OpportunitiesModule
        // For now, just navigate to opportunities
      }
    }
    // Add more action handlers as needed
  };

  const handleAIRelanceForDocument = (document) => {
    setSelectedDocument(document);
    setShowAIRelanceModal(true);
  };

  const renderTabContent = () => {
    if (!enhancedData || !conversionData) return null;

    const healthStyle = getHealthColor(healthScore);

    switch (activeTab) {
      case 'enhanced':
        return <EnhancedDashboard onNavigate={onNavigate} />;
      case 'classic':
        return renderOverviewTab(healthStyle);
      case 'performance':
        return renderPerformanceTab();
      case 'pipeline':
        return renderPipelineTab();
      default:
        return <EnhancedDashboard onNavigate={onNavigate} />;
    }
  };

  const renderOverviewTab = (healthStyle) => (
    <div className="space-y-6">
      {/* Global Health Score */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">🏥 Score de Santé Global</h3>
          <div className={`px-4 py-2 rounded-full ${healthStyle.bg} ${healthStyle.text} ${healthStyle.ring} ring-2`}>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${healthStyle.indicator}`}></div>
              <span className="font-bold text-2xl">{healthScore}</span>
              <span className="text-sm">/100</span>
            </div>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className={`h-4 rounded-full ${healthStyle.indicator} transition-all duration-500`}
            style={{ width: `${healthScore}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          {healthScore >= 80 ? 'Excellente santé commerciale' :
           healthScore >= 60 ? 'Bonne santé, quelques améliorations possibles' :
           healthScore >= 40 ? 'Santé moyenne, attention requise' :
           'Santé critique, actions urgentes nécessaires'}
        </p>
      </div>

      {/* Priority Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🚨 Actions Prioritaires</h3>
        <div className="space-y-3">
          {priorityActions.length > 0 ? (
            priorityActions.map((action, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${
                action.priority === 'high' ? 'border-red-500 bg-red-50' :
                action.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                'border-blue-500 bg-blue-50'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">{action.icon}</span>
                    <div>
                      <h4 className="font-medium text-gray-900">{action.title}</h4>
                      <p className="text-sm text-gray-600">{action.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      action.priority === 'high' ? 'bg-red-100 text-red-800' :
                      action.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {action.priority === 'high' ? 'Urgent' : action.priority === 'medium' ? 'Important' : 'Normal'}
                    </span>
                    <button 
                      onClick={() => handlePriorityAction(action)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium relative z-10"
                    >
                      {action.action}
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <span className="text-4xl mb-2 block">✅</span>
              <p>Aucune action prioritaire nécessaire</p>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            title: 'Chiffre d\'affaires',
            value: `${(enhancedData.dashboard_stats.total_revenue || 0).toLocaleString()} FCFA`,
            change: `+${(enhancedData.dashboard_stats.revenue_this_month || 0).toLocaleString()} FCFA`,
            changeLabel: 'ce mois',
            color: 'bg-baobab-primary',
            icon: '💰'
          },
          {
            title: 'Opportunités',
            value: enhancedData.commercial_kpis.total_opportunities || 0,
            change: `+${enhancedData.commercial_kpis.opportunities_this_month || 0}`,
            changeLabel: 'ce mois',
            icon: '🎯'
          },
          {
            title: 'Opérations',
            value: enhancedData.operational_kpis.total_operations || 0,
            change: `+${enhancedData.operational_kpis.operations_this_month || 0}`,
            changeLabel: 'ce mois',
            icon: '📦'
          },
          {
            title: 'Taux de conversion',
            value: `${enhancedData.commercial_kpis.conversion_rate.toFixed(1)}%`,
            change: `Pipeline: ${(enhancedData.commercial_kpis.pipeline_value || 0).toLocaleString()} FCFA`,
            changeLabel: 'en cours',
            icon: '📈'
          }
        ].map((card, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                <p className="text-sm text-gray-500 mt-1">
                  <span className="text-green-600 font-medium">{card.change}</span> {card.changeLabel}
                </p>
              </div>
              <div className="w-12 h-12 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center text-gray-600 text-xl">
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPerformanceTab = () => (
    <div className="space-y-6">
      {/* KPIs Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Commercial KPIs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">📊</span>
              KPIs Commerciaux
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{enhancedData.commercial_kpis.won_opportunities}</p>
                <p className="text-sm text-gray-600">Opportunités gagnées</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{enhancedData.commercial_kpis.lost_opportunities}</p>
                <p className="text-sm text-gray-600">Opportunités perdues</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-baobab-primary">
                {(enhancedData.commercial_kpis.average_deal_value || 0).toLocaleString()} FCFA
              </p>
              <p className="text-sm text-gray-600">Valeur moyenne par deal</p>
            </div>
            
            {/* Opportunities by Status */}
            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Répartition par statut</h4>
              <div className="space-y-2">
                {Object.entries(enhancedData.commercial_kpis.opportunities_by_status).map(([status, count]) => (
                  <div key={status} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 capitalize">{status}</span>
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Operational KPIs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🔧</span>
              KPIs Opérationnels
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{enhancedData.operational_kpis.completed_operations}</p>
                <p className="text-sm text-gray-600">Opérations terminées</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{enhancedData.operational_kpis.in_progress_operations}</p>
                <p className="text-sm text-gray-600">En cours</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-baobab-primary">
                {(enhancedData.operational_kpis.average_operation_value || 0).toLocaleString()} FCFA
              </p>
              <p className="text-sm text-gray-600">Valeur moyenne par opération</p>
            </div>
            
            {/* Operations by Priority */}
            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Répartition par priorité</h4>
              <div className="space-y-2">
                {Object.entries(enhancedData.operational_kpis.operations_by_priority).map(([priority, count]) => (
                  <div key={priority} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{priority}</span>
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Performance Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">📈</span>
            Performance Mensuelle (12 derniers mois)
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-baobab-primary">
                {enhancedData.monthly_kpis.reduce((sum, month) => sum + month.revenue, 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-600">Chiffre d'affaires total</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {enhancedData.monthly_kpis.reduce((sum, month) => sum + month.opportunities_count, 0)}
              </p>
              <p className="text-sm text-gray-600">Opportunités créées</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {enhancedData.monthly_kpis.reduce((sum, month) => sum + month.operations_count, 0)}
              </p>
              <p className="text-sm text-gray-600">Opérations réalisées</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {(enhancedData.monthly_kpis.reduce((sum, month) => sum + month.conversion_rate, 0) / enhancedData.monthly_kpis.length).toFixed(1)}%
              </p>
              <p className="text-sm text-gray-600">Taux conversion moyen</p>
            </div>
          </div>
          
          {/* Simple visual representation of monthly data */}
          <div className="mt-6">
            <h4 className="font-medium text-gray-900 mb-4">Évolution mensuelle du chiffre d'affaires</h4>
            <div className="flex items-end justify-between h-32 bg-gray-50 rounded-lg p-4">
              {enhancedData.monthly_kpis.map((month, index) => {
                const maxRevenue = Math.max(...enhancedData.monthly_kpis.map(m => m.revenue));
                const height = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
                
                return (
                  <div key={index} className="flex flex-col items-center">
                    <div 
                      className="bg-baobab-primary rounded-t-sm w-8 flex items-end justify-center"
                      style={{ height: `${height}%` }}
                    >
                    </div>
                    <div className="text-xs text-gray-600 mt-1 transform -rotate-45">
                      {month.month.split('-')[1]}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Objectives Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">🎯</span>
            Suivi des Objectifs
          </h3>
          <button
            onClick={() => openObjectiveModal()}
            className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors"
          >
            Nouvel Objectif
          </button>
        </div>
        <div className="p-6">
          {enhancedData.active_objectives.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {enhancedData.active_objectives.map((objective) => {
                const progress = calculateProgress(objective.current_value, objective.target_value);
                const progressColor = getProgressColor(progress);
                
                return (
                  <div key={objective.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 flex items-center">
                        <span className="mr-2">{getCategoryIcon(objective.category)}</span>
                        {objective.title}
                      </h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openObjectiveModal(objective)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteObjective(objective.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Progression</span>
                        <span>{progress.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${progressColor}`}
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{objective.current_value.toLocaleString()} {objective.unit}</span>
                      <span>{objective.target_value.toLocaleString()} {objective.unit}</span>
                    </div>
                    
                    <div className="mt-2 text-xs text-gray-500">
                      Échéance: {new Date(objective.deadline).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Aucun objectif défini. Cliquez sur "Nouvel Objectif" pour commencer.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderPipelineTab = () => (
    <div className="space-y-6">
      {/* Visual Pipeline */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <span className="mr-2">🔄</span>
          Pipeline Visual
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Opportunités', value: conversionData.pipeline_stages.opportunities, icon: '🎯' },
            { label: 'Devis', value: conversionData.pipeline_stages.devis, icon: '📋' },
            { label: 'Bons de commande', value: conversionData.pipeline_stages.bon_commande, icon: '🛒' },
            { label: 'Factures', value: conversionData.pipeline_stages.facture, icon: '🧾' }
          ].map((stage, index) => (
            <div key={index} className="text-center">
              <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg mb-4 hover:bg-gray-100 transition-colors">
                <div className="text-2xl mb-2 opacity-60">{stage.icon}</div>
                <div className="text-2xl font-bold text-gray-900">{stage.value}</div>
                <div className="text-sm text-gray-600">{stage.label}</div>
              </div>
              {index < 3 && (
                <div className="text-gray-400 text-2xl">
                  ⬇️
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Conversion Rate */}
        <div className="mt-6 text-center">
          <div className="inline-block bg-purple-100 px-6 py-3 rounded-lg">
            <div className="text-purple-600 font-semibold">
              Taux de conversion global: {formatPercentage(conversionData.conversion_rate)}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {conversionData.documents_with_opportunity} documents générés sur {conversionData.total_opportunities} opportunités
            </div>
          </div>
        </div>
      </div>

      {/* Recent Conversions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">💰</span>
            Conversions Récentes
          </h3>
        </div>
        <div className="p-6">
          {conversionData.recent_conversions && conversionData.recent_conversions.length > 0 ? (
            <div className="space-y-4">
              {conversionData.recent_conversions.map((conversion, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-green-100 text-green-600 rounded-lg flex items-center justify-center">
                      ✅
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{conversion.nom_opportunite}</div>
                      <div className="text-sm text-gray-500">{conversion.client_name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-900">{formatCurrency(conversion.total_value)}</div>
                    <div className="text-sm text-gray-500">{conversion.documents_count} documents</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Aucune conversion récente
            </div>
          )}
        </div>
      </div>

      {/* Documents requiring follow-up */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">📧</span>
            Documents nécessitant une relance
          </h3>
        </div>
        <div className="p-6">
          {enhancedData.dashboard_stats.recent_documents?.length > 0 ? (
            <div className="space-y-4">
              {enhancedData.dashboard_stats.recent_documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      doc.type === 'devis' ? 'bg-blue-100 text-blue-600' :
                      doc.type === 'bon_commande' ? 'bg-green-100 text-green-600' :
                      'bg-orange-100 text-orange-600'
                    }`}>
                      {doc.type === 'devis' ? '📋' : doc.type === 'bon_commande' ? '🛒' : '🧾'}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{doc.reference}</div>
                      <div className="text-sm text-gray-500">{doc.client.name}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-900">{formatCurrency(doc.total_ttc)}</div>
                      <div className="text-sm text-gray-500">{new Date(doc.created_at).toLocaleDateString('fr-FR')}</div>
                    </div>
                    <button
                      onClick={() => handleAIRelanceForDocument(doc)}
                      className="bg-blue-500 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-600 transition-colors"
                    >
                      🤖 Relance IA
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Aucun document récent
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Afficher le spinner pendant le chargement initial
  if (loading && !enhancedData && !conversionData) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p>Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  // Afficher l'erreur uniquement si le chargement est terminé ET qu'il n'y a pas de données
  if (!loading && (!enhancedData || !conversionData)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Impossible de charger les données du tableau de bord
          </h3>
          <p className="text-red-600 mb-4">
            {enhancedData?.error || 'Une erreur est survenue lors du chargement des données.'}
          </p>
          <button
            onClick={() => {
              setLoading(true);
              fetchEnhancedData();
              fetchConversionData();
            }}
            className="bg-baobab-primary text-white px-6 py-2 rounded-lg hover:bg-baobab-secondary transition-colors"
          >
            🔄 Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header avec bouton actualiser */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
        <button
          onClick={() => {
            setLoading(true);
            fetchEnhancedData();
            fetchConversionData();
            fetchObjectives();
          }}
          className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-secondary transition-colors flex items-center space-x-2"
          title="Actualiser les données"
        >
          <span>🔄</span>
          <span>Actualiser</span>
        </button>
      </div>

      {/* Enhanced Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'enhanced', name: 'Vue d\'ensemble', icon: '🚀' },
              { id: 'finance', name: 'Finance', icon: '💰' },
              { id: 'operations', name: 'Opérations', icon: '🚛' },
              { id: 'incidents', name: 'Incidents & Retards', icon: '🚨' },
              { id: 'objectives', name: 'Objectifs', icon: '🎯' },
              { id: 'classic', name: 'Vue Classique', icon: '📊' },
              { id: 'performance', name: 'Performance', icon: '📈' },
              { id: 'pipeline', name: 'Pipeline', icon: '🔄' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-baobab-primary text-baobab-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'enhanced' ? (
          <EnhancedDashboard onNavigate={onNavigate} activeTab="overview" />
        ) : activeTab === 'finance' ? (
          <EnhancedDashboard onNavigate={onNavigate} activeTab="finance" />
        ) : activeTab === 'operations' ? (
          <EnhancedDashboard onNavigate={onNavigate} activeTab="operations" />
        ) : activeTab === 'incidents' ? (
          <EnhancedDashboard onNavigate={onNavigate} activeTab="incidents" />
        ) : activeTab === 'objectives' ? (
          <ObjectivesManager 
            onObjectivesUpdate={(objectives) => {
              // Update objectives in parent state and refresh dashboard data
              setObjectives(objectives);
              fetchEnhancedData();
            }} 
          />
        ) : (
          <div className="p-6">
            {renderTabContent()}
          </div>
        )}
      </div>

      {/* AI Relance Modal */}
      {showAIRelanceModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Relance IA pour {selectedDocument.reference}</h3>
              <button
                onClick={() => setShowAIRelanceModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <AIRelanceGenerator
              documentId={selectedDocument.id}
              documentType={selectedDocument.type}
              currentStatus={selectedDocument.status || 'en_attente'}
              onRelanceGenerated={(relanceData) => {
                console.log('Relance generated:', relanceData);
                // Handle the generated relance data here
              }}
            />
          </div>
        </div>
      )}

      {/* Objective Modal */}
      {showObjectiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingObjective ? 'Modifier l\'objectif' : 'Nouvel objectif'}
              </h3>
              <button
                onClick={closeObjectiveModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titre</label>
                <input
                  type="text"
                  value={objectiveForm.title}
                  onChange={(e) => setObjectiveForm({...objectiveForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  placeholder="Titre de l'objectif"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={objectiveForm.description}
                  onChange={(e) => setObjectiveForm({...objectiveForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  rows={3}
                  placeholder="Description de l'objectif"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valeur cible</label>
                  <input
                    type="number"
                    value={objectiveForm.target_value}
                    onChange={(e) => setObjectiveForm({...objectiveForm, target_value: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                    placeholder="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Unité</label>
                  <select
                    value={objectiveForm.unit}
                    onChange={(e) => setObjectiveForm({...objectiveForm, unit: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  >
                    <option value="FCFA">FCFA</option>
                    <option value="quantity">Quantité</option>
                    <option value="percentage">Pourcentage</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                  <select
                    value={objectiveForm.category}
                    onChange={(e) => setObjectiveForm({...objectiveForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  >
                    <option value="revenue">Chiffre d'affaires</option>
                    <option value="operations">Opérations</option>
                    <option value="operations_average">Opérations moyenne</option>
                    <option value="clients_converted">Clients convertis</option>
                    <option value="opportunities">Opportunités</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
                  <select
                    value={objectiveForm.period}
                    onChange={(e) => setObjectiveForm({...objectiveForm, period: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  >
                    <option value="daily">Quotidien</option>
                    <option value="monthly">Mensuel</option>
                    <option value="quarterly">Trimestriel</option>
                    <option value="yearly">Annuel</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Échéance</label>
                  <input
                    type="date"
                    value={objectiveForm.deadline}
                    onChange={(e) => setObjectiveForm({...objectiveForm, deadline: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeObjectiveModal}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={editingObjective ? handleUpdateObjective : handleCreateObjective}
                className="px-4 py-2 bg-baobab-primary text-white rounded-lg hover:bg-baobab-dark"
              >
                {editingObjective ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;